import { useCallback, useEffect, useState } from "react"

import {
  Button,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
} from "@coval/ui"
import { AlertTriangle, Plus, Sparkles, Trash2 } from "lucide-react"

import type { IVRFlow } from "@/types/IVRFlow"

import { JsonCodeEditor } from "../JsonCodeEditor"
import { detectNodeKind } from "./ivr-flow-tree-ops"
import { exampleFlow } from "./ivr-flow-utils"
import { IVRFlowPreview } from "./IVRFlowPreview"
import { NodeEditor } from "./NodeEditor"
import { useIVRFlowState } from "./useIVRFlowState"
import { useIVRGeneration } from "./useIVRGeneration"
import { useIVRJsonEditor } from "./useIVRJsonEditor"

interface IVRTreeBuilderProps {
  /** Initial flow to populate the builder with. Treated as a starting value
   * (uncontrolled); subsequent edits live in component state until saved. */
  initialFlow?: IVRFlow | null
  /** Invoked when the user clicks Save. If omitted, the Save button is hidden
   * and the builder behaves as a local scratchpad. */
  onIvrFlowSave?: (flow: IVRFlow) => void | Promise<void>
  /** Fires on every internal flow mutation. Useful when the parent needs to
   * mirror the flow state (e.g. a modal that runs an action against it). */
  onFlowChange?: (flow: IVRFlow | null) => void
  /** Owner-provided generator so the shared builder does not depend on app modules. */
  generateIvrFlow?: (prompt: string) => Promise<IVRFlow>
  /** Optional label override for the save button. */
  saveLabel?: string
}

export const IVRTreeBuilder = ({
  initialFlow,
  onIvrFlowSave,
  onFlowChange,
  generateIvrFlow,
  saveLabel = "Save IVR flow",
}: IVRTreeBuilderProps = {}) => {
  const {
    flow,
    selectedNodeId,
    setSelectedNodeId,
    validation,
    reactFlowData,
    selectedNode,
    selectedOutgoingEdges,
    replaceFlow,
    clearFlow,
    updateNodeProps,
    setNodeKind,
    addEdgeForSource,
    updateEdge,
    removeEdge,
    handleNodeConnect,
    handleNodePositionChange,
    addBlankNode,
    deleteNode,
    setStartNode,
    renameNode,
  } = useIVRFlowState(initialFlow, onFlowChange)

  const [isSaving, setIsSaving] = useState(false)
  const [tab, setTab] = useState<"workflow" | "generate" | "json">("workflow")
  const canGenerate = Boolean(generateIvrFlow)

  useEffect(() => {
    if (!canGenerate && tab === "generate") {
      setTab("workflow")
    }
  }, [canGenerate, tab])

  const json = useIVRJsonEditor(replaceFlow)

  const loadExample = useCallback(() => {
    replaceFlow(exampleFlow())
    setTab("workflow")
  }, [replaceFlow])

  const handleGenerated = useCallback(
    (generated: IVRFlow) => {
      replaceFlow(generated)
      setTab("workflow")
    },
    [replaceFlow],
  )

  const { generatePrompt, setGeneratePrompt, isGenerating, handleGenerate } =
    useIVRGeneration({
      generateIvrFlow,
      onLoadExample: loadExample,
      onGenerated: handleGenerated,
    })

  const handleClearFlow = () => {
    clearFlow()
    json.reset()
  }

  const handleSave = async () => {
    if (!flow || !onIvrFlowSave) {
      return
    }
    if (!validation.valid) {
      toast.danger({
        title: "Cannot save",
        description: validation.issues[0] ?? "IVR flow is invalid.",
      })
      return
    }
    setIsSaving(true)
    try {
      await onIvrFlowSave(flow)
    } finally {
      setIsSaving(false)
    }
  }

  const enterTab = (next: "workflow" | "generate" | "json") => {
    if (next === "json" && flow) {
      json.syncFromFlow(flow)
    }
    setTab(next)
  }

  return (
    <div className="w-full space-y-4">
      {flow && (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="ghost" onClick={handleClearFlow}>
            <Trash2 className="w-4 h-4 mr-1" />
            Clear
          </Button>
          {onIvrFlowSave && (
            <Button
              type="button"
              onClick={() => {
                void handleSave()
              }}
              disabled={isSaving || !validation.valid}
              className="ml-auto"
            >
              {isSaving ? "Saving…" : saveLabel}
            </Button>
          )}
        </div>
      )}

      {flow && !validation.valid && (
        <div className="flex items-start gap-2 p-2 bg-coval-warning-surface border border-coval-warning-border rounded-md">
          <AlertTriangle className="w-4 h-4 text-coval-warning-on-surface shrink-0 mt-0.5" />
          <div className="text-xs">
            <p className="font-medium text-coval-warning-on-surface">
              IVR flow has issues
            </p>
            <ul className="text-coval-warning-on-surface mt-0.5 list-disc list-inside">
              {validation.issues.slice(0, 5).map((issue, i) => (
                <li key={i}>{issue}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <Tabs
        value={tab}
        onValueChange={(v) => enterTab(v as "workflow" | "generate" | "json")}
        className="flex flex-col"
      >
        <TabsList
          variant="underline"
          className="w-full justify-start border-b border-coval-border rounded-none"
        >
          <TabsTrigger value="workflow">Workflow</TabsTrigger>
          <TabsTrigger value="json">JSON</TabsTrigger>
          {canGenerate && <TabsTrigger value="generate">Generate</TabsTrigger>}
        </TabsList>

        <TabsContent value="workflow" className="mt-3">
          {flow ? (
            <div className="h-[620px] flex gap-3">
              <div className="flex-1 basis-1/2 relative min-w-0">
                <IVRFlowPreview
                  nodes={reactFlowData.nodes}
                  edges={reactFlowData.edges}
                  selectedNodeId={selectedNodeId}
                  onSelectNode={setSelectedNodeId}
                  onConnect={handleNodeConnect}
                  onDeleteNode={deleteNode}
                  onPositionChange={handleNodePositionChange}
                />
                <div className="absolute top-2 right-2 z-10 flex items-center gap-2 bg-surface-0/90 backdrop-blur-sm border border-coval-border rounded-md p-1 shadow-sm">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={addBlankNode}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add node
                  </Button>
                </div>
              </div>

              <div className="flex-1 basis-1/2 min-w-0 h-full overflow-y-auto rounded-lg border border-coval-border bg-surface-0">
                {selectedNode ? (
                  <div className="p-3">
                    <NodeEditor
                      node={selectedNode}
                      kind={detectNodeKind(
                        selectedNode,
                        selectedOutgoingEdges.length,
                      )}
                      allNodeIds={flow.nodes.map((n) => n.id)}
                      outgoingEdges={selectedOutgoingEdges}
                      isStart={selectedNode.id === flow.start_node}
                      onUpdateNode={(patch) =>
                        updateNodeProps(selectedNode.id, patch)
                      }
                      onRenameNode={(nextId) =>
                        renameNode(selectedNode.id, nextId)
                      }
                      onSetKind={(kind) => setNodeKind(selectedNode.id, kind)}
                      onDelete={() => deleteNode(selectedNode.id)}
                      onSetStart={() => setStartNode(selectedNode.id)}
                      onAddEdge={() => addEdgeForSource(selectedNode.id)}
                      onUpdateEdge={updateEdge}
                      onRemoveEdge={removeEdge}
                    />
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center text-xs text-coval-text-muted px-4 gap-2">
                    <p className="font-medium text-coval-text">Select a node</p>
                    <p>
                      Click a node in the canvas to edit it. Drag from a node's
                      bottom handle to another node to add an edge or
                      auto-advance.
                    </p>
                    <p className="mt-2">
                      {flow.nodes.length} node
                      {flow.nodes.length === 1 ? "" : "s"} · {flow.edges.length}{" "}
                      edge
                      {flow.edges.length === 1 ? "" : "s"}.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-coval-border bg-surface-1 px-4 py-10 text-center text-sm text-coval-text-muted flex flex-col items-center gap-3">
              <Sparkles className="w-8 h-8 text-coval-text-muted" />
              <p>
                {canGenerate ? (
                  <>
                    Use the <b>Generate</b> tab to create a tree from a
                    description, or add your first node to start building.
                  </>
                ) : (
                  "Add your first node to start building, or paste a flow in JSON."
                )}
              </p>
              <Button type="button" variant="secondary" onClick={addBlankNode}>
                <Plus className="w-4 h-4 mr-1" />
                Add node
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="json" className="mt-3">
          <div className="h-[620px] flex flex-col gap-3">
            {(json.jsonError || json.jsonIssues.length > 0) && (
              <div className="p-2 bg-coval-danger-surface border border-coval-danger-border rounded-md text-xs shrink-0">
                <p className="font-medium text-coval-danger-on-surface">
                  {json.jsonError}
                </p>
                {json.jsonIssues.length > 0 && (
                  <ul className="text-coval-danger-on-surface mt-1 list-disc list-inside">
                    {json.jsonIssues.map((issue, i) => (
                      <li key={i}>{issue}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <JsonCodeEditor
              ariaLabel="IVR flow JSON editor"
              className="flex-1 min-h-0"
              height="100%"
              value={json.jsonDraft}
              onChange={json.setJsonDraft}
            />
            <div className="flex justify-end shrink-0">
              <Button type="button" onClick={json.applyJsonDraft}>
                Apply JSON
              </Button>
            </div>
          </div>
        </TabsContent>

        {canGenerate && (
          <TabsContent value="generate" className="mt-3">
            <div className="rounded-lg border border-coval-border bg-surface-0 p-3 space-y-2">
              <Label className="text-xs" htmlFor="ivr-generate-prompt">
                Describe the IVR you want
              </Label>
              <Textarea
                id="ivr-generate-prompt"
                aria-label="IVR generation prompt"
                value={generatePrompt}
                onChange={(e) => setGeneratePrompt(e.target.value)}
                rows={8}
                placeholder={`e.g. A bank IVR that greets the caller, asks them to press 1 for account access (verify with phone + PIN, then offer balance / recent transactions / transfer) or 2 for a new account. Falls back to an operator if the caller says "operator".`}
                disabled={isGenerating}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                    e.preventDefault()
                    void handleGenerate()
                  }
                }}
              />
              <p className="text-xs text-coval-text-muted">
                Tip: type <b>create example tree</b> for a built-in sample —
                otherwise describe your IVR in whatever form works for you.
              </p>
              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={() => {
                    void handleGenerate()
                  }}
                  disabled={isGenerating || !generatePrompt.trim()}
                >
                  <Sparkles className="w-4 h-4 mr-1" />
                  {isGenerating ? "Generating…" : "Generate"}
                </Button>
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
